import { createFileRoute } from '@tanstack/react-router';
import { ArrowLeft } from '@/lib/icons';
import { Button } from '@/components/ui/button';

// Keep in lockstep with ``TERMS_VERSION`` in
// ``services/offerings-service/app/common/constants/consent.py`` and
// ``clients/mypl-app/src/lib/hooks/use-customer-consent.tsx``. The
// backend rejects ``POST /v1/customers/`` whose ``consent.version``
// doesn't match the server-side constant, so a stale portal build will
// fail loudly rather than record acceptance against superseded text.
export const TERMS_VERSION = '1.0.0';

type Section = Readonly<{
  id: string;
  group: 'terms' | 'privacy';
  title: string;
  intro: string;
  bullets: readonly string[];
}>;

// Mirrors the section content rendered in the mobile app's
// ``TermsAcceptSheet``. Both renderings must show the same text for a
// given ``TERMS_VERSION`` — bump them together or audit becomes
// inconsistent across channels.
const SECTIONS: readonly Section[] = [
  {
    id: 'service',
    group: 'terms',
    title: 'Service',
    intro:
      'Mazeed Pay is a Sharia-compliant device financing service. Every plan is structured as Murabaha — a transparent cost-plus-profit sale, with no interest (riba), no compounding, and no hidden fees.',
    bullets: [
      'Finance the latest smartphones with structured installment plans paid via mobile money.',
      'Total price, profit margin, and full payment schedule are disclosed before you sign.',
      'Operated by Mazeed Pay in partnership with licensed merchant branches across Somalia.',
      'All products are reviewed by our Sharia advisory board for ongoing compliance.',
    ],
  },
  {
    id: 'eligibility',
    group: 'terms',
    title: 'Eligibility',
    intro:
      'To qualify for an Mazeed Pay financing plan, the customer must meet the following minimum requirements:',
    bullets: [
      'Is 18 years of age or older.',
      'Holds a valid Somali national ID or passport.',
      'Uses an active mobile money wallet — EVC Plus, Zaad, Sahal, or M-Pesa.',
      'Their phone line has been active for at least 3 months under their name.',
      'They pass the Mazeed Pay credit assessment, which considers usage history, mobile money activity, balance trends, and SIM stability.',
    ],
  },
  {
    id: 'how-to-join',
    group: 'terms',
    title: 'How to Join',
    intro:
      'Customers can sign up through any channel — the mobile app, USSD `*123#`, or in person at a branch with an agent. The process completes in a few minutes regardless of the channel.',
    bullets: [
      'Verify identity via OTP (app), SIM (USSD), or in-person ID check (branch).',
      'Confirm name and provide an optional photo.',
      'Review and grant the data-sharing consents needed for credit assessment.',
      'Browse the device catalogue, choose a plan, and pick a merchant branch.',
      'Submit the application and receive an instant credit decision.',
    ],
  },
  {
    id: 'collect',
    group: 'terms',
    title: 'Collect',
    intro:
      'Once approved, the device is reserved at the merchant branch selected during application.',
    bullets: [
      'Visit the selected merchant branch within 7 days of approval.',
      'Bring a valid government-issued ID matching the Mazeed Pay profile.',
      'Pay the agreed down-payment to release the device for collection.',
      'The contract and payment schedule appear in the customer-facing channels immediately after collection.',
      'Reservations expire after 7 days and the device returns to inventory if not collected.',
    ],
  },
  {
    id: 'repayment',
    group: 'terms',
    title: 'Repayment',
    intro:
      'Pay installments via mobile money — no queues, no paperwork, no extra fees.',
    bullets: [
      'Installments are due on the date stated in the contract.',
      'Pay in full or in part — partial payments are reflected immediately.',
      'No interest. No late fees. The customer only ever owes the agreed total price.',
      'Early payoff is encouraged and shortens the plan to its final balance.',
      'Each successful payment triggers a receipt and confirmation notification.',
    ],
  },
  {
    id: 'reminders',
    group: 'terms',
    title: 'Reminders',
    intro:
      'Mazeed Pay helps customers stay on top of their plan with timely reminders before each due date.',
    bullets: [
      'A reminder is sent the day before each installment is due.',
      'Real-time confirmations appear after every successful payment.',
      'If a payment becomes overdue, urgent alerts are sent to help avoid restrictions.',
      'Customers can adjust notification preferences any time from Settings → Notifications in the app.',
    ],
  },
  {
    id: 'service-denial',
    group: 'terms',
    title: 'Service Denial',
    intro:
      'In limited circumstances, Mazeed Pay may apply restrictions or decline service. These steps are progressive and always reversible by bringing the account current.',
    bullets: [
      'Warning: a friendly notice is shown when an installment is about to become overdue.',
      'Partial restriction: certain device features may be limited if a payment remains unpaid.',
      'Full lock: continued non-payment may temporarily restrict device usage, in line with the contract.',
      'Default: persistent non-payment may end the agreement and trigger collection in line with applicable law.',
      'Restrictions are lifted automatically once the account is brought current.',
      'Applications can be declined if eligibility criteria or credit assessment thresholds are not met.',
    ],
  },
  {
    id: 'warranty',
    group: 'terms',
    title: 'Warranty',
    intro:
      'Every device sold through Mazeed Pay is covered by the manufacturer warranty, plus in-app support throughout the plan.',
    bullets: [
      'The manufacturer warranty applies as stated by the device maker (typically 12 months).',
      'Hardware faults during the warranty period are handled at the merchant branch where the device was collected.',
      'Damage caused by misuse, liquid, drops, or unauthorised repair is not covered.',
      'Mazeed Pay support is available in-app to help raise warranty claims and track resolution.',
      'On full payoff, the manufacturer warranty (if still valid) transfers fully to the customer with the device.',
    ],
  },
  {
    id: 'privacy',
    group: 'privacy',
    title: 'Privacy & Data',
    intro:
      'Your data is yours. Mazeed Pay collects only what is required to assess eligibility and run the plan, and never sells it.',
    bullets: [
      'Name, phone number, ID details, and mobile-money usage signals are collected to score and process the application.',
      'Data is encrypted in transit and at rest, and access is restricted to authorised Mazeed Pay staff.',
      'Data is shared only with regulated payment processors and credit-scoring partners required to operate the plan.',
      'Customers can request access, correction, or deletion of their data anytime by emailing privacy@idaymi.com.',
      'Notification and marketing preferences can be adjusted from Settings → Notifications in the app.',
    ],
  },
];

function TermsPage() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b bg-card">
        <div className="mx-auto max-w-3xl px-4 py-4 flex items-center justify-between gap-4">
          <Button variant="ghost" size="sm" asChild>
            <a href="/" className="inline-flex items-center gap-1.5">
              <ArrowLeft className="h-4 w-4" />
              <span>Back</span>
            </a>
          </Button>
          <div className="text-xs text-muted-foreground">
            Version <span className="font-mono font-semibold">{TERMS_VERSION}</span>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-4 py-8 space-y-8">
        <div className="space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">
            Mazeed Pay Terms &amp; Conditions
          </h1>
          <p className="text-sm text-muted-foreground">
            The same terms apply across every channel — mobile app, USSD, and
            branch counter. Bump the version when content changes; the backend
            rejects new acceptances against superseded text.
          </p>
        </div>

        <nav aria-label="Sections" className="rounded-lg border bg-muted/30 p-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground/70 mb-2">
            Contents
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-sm">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-primary hover:underline underline-offset-2"
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {SECTIONS.map((section) => (
          <section
            key={section.id}
            id={section.id}
            className="space-y-3 scroll-mt-20"
          >
            <div className="space-y-1">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                {section.group === 'privacy' ? 'Privacy' : 'Terms'}
              </div>
              <h2 className="text-xl font-semibold">{section.title}</h2>
            </div>
            <p className="text-sm leading-relaxed text-muted-foreground">
              {section.intro}
            </p>
            <ul className="space-y-1.5 text-sm leading-relaxed">
              {section.bullets.map((b, i) => (
                <li key={i} className="flex gap-2">
                  <span
                    aria-hidden="true"
                    className="mt-1.5 inline-block h-1.5 w-1.5 flex-shrink-0 rounded-full bg-primary/70"
                  />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        <footer className="border-t pt-6 text-xs text-muted-foreground">
          Questions about these terms? Contact{' '}
          <a
            href="mailto:support@idaymi.com"
            className="text-primary underline-offset-2 hover:underline"
          >
            support@idaymi.com
          </a>
          .
        </footer>
      </main>
    </div>
  );
}

export const Route = createFileRoute('/terms')({
  component: TermsPage,
});
